import { Helmet } from 'react-helmet-async';
import React from 'react';

/**
 * Safe JSON-LD serializer — escapes </script> sequences that could break out
 * of a <script type="application/ld+json"> tag and inject raw HTML.
 * Without this, user-controlled strings containing "</script>" in structured
 * data fields (title, description, author…) would be valid XSS vectors.
 *
 * < / > are valid JSON Unicode escapes and are parsed correctly
 * by all JSON-LD consumers, including Google's Rich Results crawler.
 */
export function safeJsonLd(data: unknown): string {
  return JSON.stringify(data)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');
}

interface Organization {
  name: string;
  url: string;
  logo: string;
  sameAs?: string[];
}

const defaultOrganization: Organization = {
  name: "recruteurs.io",
  url: "https://recruteurs.io",
  logo: "https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/assets//Recruteurs.io%20Logo%20Final-01.png",
  sameAs: [
    "https://twitter.com/recruteurs_io",
    "https://www.linkedin.com/company/recruteurs-io",
    "https://www.facebook.com/recruteurs.io"
  ]
};

export function OrganizationSchema({ organization = defaultOrganization }: { organization?: Organization }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: organization.name,
    url: organization.url,
    logo: organization.logo,
    sameAs: organization.sameAs
  };

  return (
    <Helmet>
      <script type="application/ld+json">{safeJsonLd(schema)}</script>
    </Helmet>
  );
}

interface WebPage {
  title: string;
  description: string;
  url: string;
  lastReviewed?: string;
  speakable?: {
    cssSelector: string;
  };
}

export function WebPageSchema({ page }: { page: WebPage }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: page.title,
    description: page.description,
    url: page.url,
    ...(page.lastReviewed && { datePublished: page.lastReviewed }),
    ...(page.speakable && {
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: page.speakable.cssSelector
      }
    })
  };

  return (
    <Helmet>
      <script type="application/ld+json">{safeJsonLd(schema)}</script>
    </Helmet>
  );
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

export function BreadcrumbSchema({ items }: { items: BreadcrumbItem[] }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@id": item.url,
        name: item.name
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">{safeJsonLd(schema)}</script>
    </Helmet>
  );
}

interface Article {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  author: {
    name: string;
    url?: string;
  };
  publisher: Organization;
  url: string;
  mainEntityOfPage: string;
}

export function ArticleSchema({ article }: { article: Article }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.headline,
    description: article.description,
    image: article.image,
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    author: {
      "@type": "Person",
      name: article.author.name,
      ...(article.author.url && { url: article.author.url })
    },
    publisher: {
      "@type": "Organization",
      name: article.publisher.name,
      logo: {
        "@type": "ImageObject",
        url: article.publisher.logo
      }
    },
    url: article.url,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": article.mainEntityOfPage
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">{safeJsonLd(schema)}</script>
    </Helmet>
  );
}

interface FAQPage {
  questions: Array<{
    question: string;
    answer: string;
  }>;
}

export function FAQPageSchema({ questions }: FAQPage) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: questions.map(q => ({
      "@type": "Question",
      name: q.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: q.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">{safeJsonLd(schema)}</script>
    </Helmet>
  );
}

interface LocalBusiness {
  name?: string;
  url?: string;
  logo?: string;
  description?: string;
  telephone?: string;
  email?: string;
  priceRange?: string;
  address?: {
    streetAddress?: string;
    addressLocality?: string;
    addressRegion?: string;
    postalCode?: string;
    addressCountry?: string;
  };
  areaServed?: string[];
  sameAs?: string[];
}

const defaultLocalBusiness: LocalBusiness = {
  name: "recruteurs.io",
  url: "https://recruteurs.io",
  logo: "https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/assets//Recruteurs.io%20Logo%20Final-01.png",
  description:
    "Cabinet de recrutement à Marseille alliant expertise RH et IA. Recrutement, marque employeur, onboarding : des solutions innovantes pour vos enjeux RH.",
  priceRange: "€€",
  address: {
    addressLocality: "Marseille",
    addressRegion: "Provence-Alpes-Côte d'Azur",
    postalCode: "13008",
    addressCountry: "FR",
  },
  areaServed: ["Marseille", "Aix-en-Provence", "PACA", "France"],
  sameAs: [
    "https://www.linkedin.com/company/recruteurs-io",
    "https://twitter.com/recruteurs_io",
    "https://www.facebook.com/recruteurs.io",
  ],
};

export function LocalBusinessSchema({
  business = defaultLocalBusiness,
}: {
  business?: LocalBusiness;
}) {
  const merged = { ...defaultLocalBusiness, ...business };
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": ["Organization", "LocalBusiness", "EmploymentAgency"],
    name: merged.name,
    url: merged.url,
    logo: merged.logo,
    description: merged.description,
    priceRange: merged.priceRange,
    areaServed: merged.areaServed,
    sameAs: merged.sameAs,
  };
  if (merged.telephone) schema.telephone = merged.telephone;
  if (merged.email) schema.email = merged.email;
  if (merged.address) {
    schema.address = {
      "@type": "PostalAddress",
      ...merged.address,
    };
  }

  return (
    <Helmet>
      <script type="application/ld+json">{safeJsonLd(schema)}</script>
    </Helmet>
  );
}

// ── PRODUCT SCHEMA ──────────────────────────────────────────────────────────
// Pour les pages qui vendent un "produit / service nommé" comme
// la Plateforme Parcours Découverte. Schema.org/Product est accepté
// par Google pour les services proprio + permet l'affichage des prix
// / aggregateRating dans les rich snippets.

interface Product {
  name: string;
  description: string;
  url: string;
  image?: string;
  brand?: string;
  category?: string;
  offers?: {
    priceCurrency: string;
    lowPrice?: string;
    highPrice?: string;
    availability?: 'InStock' | 'OutOfStock' | 'PreOrder';
  };
  aggregateRating?: {
    ratingValue: string;
    reviewCount: string;
    bestRating?: string;
    worstRating?: string;
  };
}

export function ProductSchema({ product }: { product: Product }) {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    url: product.url,
  };
  if (product.image) schema.image = product.image;
  if (product.brand) {
    schema.brand = { '@type': 'Brand', name: product.brand };
  }
  if (product.category) schema.category = product.category;
  if (product.offers) {
    const off = product.offers;
    if (off.lowPrice && off.highPrice) {
      schema.offers = {
        '@type': 'AggregateOffer',
        priceCurrency: off.priceCurrency,
        lowPrice: off.lowPrice,
        highPrice: off.highPrice,
        availability: `https://schema.org/${off.availability ?? 'InStock'}`,
      };
    } else if (off.lowPrice) {
      schema.offers = {
        '@type': 'Offer',
        priceCurrency: off.priceCurrency,
        price: off.lowPrice,
        availability: `https://schema.org/${off.availability ?? 'InStock'}`,
      };
    }
  }
  if (product.aggregateRating) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: product.aggregateRating.ratingValue,
      reviewCount: product.aggregateRating.reviewCount,
      bestRating: product.aggregateRating.bestRating ?? '5',
      worstRating: product.aggregateRating.worstRating ?? '1',
    };
  }

  return (
    <Helmet>
      <script type="application/ld+json">{safeJsonLd(schema)}</script>
    </Helmet>
  );
}
